/**
 * EngineKeySettings -- HMAC key self-service for the deid-aws risk engine.
 *
 * Collapsed-by-default panel above the Analyze button on RiskPage. Users
 * generally do not need this: the engine auto-mints a key transparently
 * on first analysis. This surface is for Bobby (and future BB users) who
 * want to configure their Claude Desktop deid-gateway client with their
 * key BEFORE first web-app use, or rotate a compromised key.
 *
 * The secret is only ever shown once -- on fresh mint or after regenerate.
 * The component keeps it in local state and clears it on collapse.
 */

import React, { useCallback, useEffect, useState } from 'react';
import * as engineKeyService from '../../services/engineKeyService';
import './EngineKeySettings.css';

interface Props {
  token: string | null;
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'no-key' }
  | { kind: 'has-key'; keyId: string; createdAt?: string; lastUsedAt?: string | null }
  | {
      kind: 'just-generated';
      keyId: string;
      secret: string;
      createdAt: string;
      wasRotation: boolean;
      previousKeyId?: string | null;
    };

function truncateKeyId(keyId: string): string {
  if (keyId.length <= 12) return keyId;
  return `${keyId.slice(0, 8)}...${keyId.slice(-4)}`;
}

function formatTs(iso?: string | null): string {
  if (!iso) return 'never';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const EngineKeySettings: React.FC<Props> = ({ token }) => {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [confirmingRegen, setConfirmingRegen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) {
      setState({ kind: 'error', message: 'Not authenticated.' });
      return;
    }
    setState({ kind: 'loading' });
    try {
      const meta = await engineKeyService.getCurrent(token);
      if (!meta.key_id) {
        setState({ kind: 'no-key' });
      } else {
        setState({
          kind: 'has-key',
          keyId: meta.key_id,
          createdAt: meta.created_at,
          lastUsedAt: meta.last_used_at,
        });
      }
    } catch (e: any) {
      setState({ kind: 'error', message: e?.message || 'Failed to load key.' });
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function handleGenerate() {
    if (!token) return;
    setBusy(true);
    try {
      const result = await engineKeyService.generate(token);
      if ('secret' in result) {
        setState({
          kind: 'just-generated',
          keyId: result.key_id,
          secret: result.secret,
          createdAt: result.created_at,
          wasRotation: false,
        });
      } else {
        // Idempotent -- key already existed; fall back to metadata view.
        await refresh();
      }
    } catch (e: any) {
      setState({ kind: 'error', message: e?.message || 'Generate failed.' });
    } finally {
      setBusy(false);
    }
  }

  async function handleRegenerate() {
    if (!token) return;
    setConfirmingRegen(false);
    setBusy(true);
    try {
      const result = await engineKeyService.regenerate(token);
      setState({
        kind: 'just-generated',
        keyId: result.key_id,
        secret: result.secret,
        createdAt: result.created_at,
        wasRotation: true,
        previousKeyId: result.regenerated_from,
      });
    } catch (e: any) {
      setState({ kind: 'error', message: e?.message || 'Regenerate failed.' });
    } finally {
      setBusy(false);
    }
  }

  async function copySecret(secret: string) {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard blocked; leave secret visible for manual copy
    }
  }

  function handleToggle(e: React.SyntheticEvent<HTMLDetailsElement>) {
    if (!e.currentTarget.open) {
      // Collapsed -- clear any surfaced secret so it doesn't linger in memory.
      if (state.kind === 'just-generated') {
        refresh();
      }
      setCopied(false);
    }
  }

  return (
    <details className="engine-key-panel" onToggle={handleToggle}>
      <summary className="engine-key-panel-summary">
        Engine Key (advanced)
      </summary>

      <div className="engine-key-panel-body">
        {state.kind === 'loading' && <p>Loading key status...</p>}

        {state.kind === 'error' && (
          <p className="engine-key-error">{state.message}</p>
        )}

        {state.kind === 'no-key' && (
          <>
            <p>
              No engine key on file. The risk engine will mint one automatically
              on your first Analyze Risk run. Generate it here first if you plan
              to use the same key from a Claude Desktop deid-gateway client.
            </p>
            <button
              type="button"
              className="engine-key-btn"
              onClick={handleGenerate}
              disabled={busy}
            >
              {busy ? 'Generating...' : 'Generate Key'}
            </button>
          </>
        )}

        {state.kind === 'has-key' && (
          <>
            <dl className="engine-key-meta">
              <dt>Key ID</dt>
              <dd>
                <code>{truncateKeyId(state.keyId)}</code>
              </dd>
              <dt>Created</dt>
              <dd>{formatTs(state.createdAt)}</dd>
              <dt>Last used</dt>
              <dd>{formatTs(state.lastUsedAt)}</dd>
            </dl>
            <button
              type="button"
              className="engine-key-btn engine-key-btn-danger"
              onClick={() => setConfirmingRegen(true)}
              disabled={busy}
            >
              Regenerate Key
            </button>

            {confirmingRegen && (
              <div className="engine-key-confirm">
                <p>
                  Regenerating invalidates your current key in any Claude Desktop
                  client that uses it. Continue?
                </p>
                <div className="engine-key-confirm-actions">
                  <button
                    type="button"
                    className="engine-key-btn engine-key-btn-danger"
                    onClick={handleRegenerate}
                    disabled={busy}
                  >
                    {busy ? 'Rotating...' : 'Yes, rotate now'}
                  </button>
                  <button
                    type="button"
                    className="engine-key-btn"
                    onClick={() => setConfirmingRegen(false)}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {state.kind === 'just-generated' && (
          <>
            <div className="engine-key-warning-banner">
              Copy this secret now -- it will not be shown again. Paste it into
              your Claude Desktop deid-gateway config if you use it there.
            </div>
            <dl className="engine-key-meta">
              <dt>Key ID</dt>
              <dd>
                <code>{state.keyId}</code>
              </dd>
              <dt>Secret</dt>
              <dd>
                <code className="engine-key-secret">{state.secret}</code>
              </dd>
              <dt>Created</dt>
              <dd>{formatTs(state.createdAt)}</dd>
              {state.wasRotation && state.previousKeyId && (
                <>
                  <dt>Replaces</dt>
                  <dd>
                    <code>{truncateKeyId(state.previousKeyId)}</code> (now invalid)
                  </dd>
                </>
              )}
            </dl>
            <div className="engine-key-actions">
              <button
                type="button"
                className="engine-key-btn"
                onClick={() => copySecret(state.secret)}
              >
                {copied ? 'Copied' : 'Copy secret'}
              </button>
              <button
                type="button"
                className="engine-key-btn engine-key-btn-secondary"
                onClick={() => refresh()}
              >
                I have copied it, hide
              </button>
            </div>
          </>
        )}
      </div>
    </details>
  );
};

export default EngineKeySettings;
